import { useRef } from 'react';
import { SearchFacet, SearchResult } from '#server/models';
import {
  CourseResultCard,
  EventResultCard,
  NewsResultCard,
  PageResultCard,
  PDFResultCard,
  PeopleResultCard,
  PodcastResultCard,
  ResearchResultCard,
  VideoResultCard,
  ResultsList,
  ResultsHeader,
} from '@/components';
import { useSearchContext } from '@/context';
import { useAnalytics, useInteractionAnalytics } from '@/hooks';

export interface ResultsMainProps {
  showRegion?: boolean;
}

const renderResultCard = (result: SearchResult, facet: SearchFacet) => {
  switch (result.contentType) {
    case 'Page':
      return <PageResultCard key={`${result.id}-${facet}`} result={result} />;
    case 'News':
      return <NewsResultCard key={`${result.id}-${facet}`} result={result} />;
    case 'Course':
      return <CourseResultCard key={`${result.id}-${facet}`} result={result} showCourseBadge={facet === 'all'} />;
    case 'Event':
      return <EventResultCard key={`${result.id}-${facet}`} result={result} />;
    case 'Video':
      return <VideoResultCard key={`${result.id}-${facet}`} result={result} />;
    case 'Podcast':
      return <PodcastResultCard key={`${result.id}-${facet}`} result={result} />;
    case 'PDF':
      return <PDFResultCard key={`${result.id}-${facet}`} result={result} />;
    case 'People':
      return <PeopleResultCard key={`${result.id}-${facet}`} result={result} />;
    case 'Research':
      return <ResearchResultCard key={`${result.id}-${facet}`} result={result} />;
    default:
      return null;
  }
};

export const ResultsMain = ({ showRegion }: ResultsMainProps) => {
  const { searchResults, loading, error, setPage, facet } = useSearchContext();

  useAnalytics();
  const { resultsContainerRef } = useInteractionAnalytics();

  const currentPage = searchResults?.page ?? 1;
  const pageSize = searchResults?.pageSize ?? 10;
  const resultCount = searchResults?.resultCount ?? 0;

  const resultsHeaderRef = useRef<HTMLDivElement | null>(null);

  return (
    <div className="flex scroll-mt-[125px] flex-col gap-6" ref={resultsContainerRef}>
      <ResultsHeader
        currentPage={currentPage}
        itemsPerPage={pageSize}
        totalResults={resultCount}
        facet={facet}
        showRegion={showRegion}
        ref={resultsHeaderRef}
      />
      <ResultsList
        searchResults={searchResults}
        loading={loading}
        error={error}
        setPage={setPage}
        facet={facet}
        resultToCardMapper={(result) => renderResultCard(result, facet)}
        resultsHeaderRef={resultsHeaderRef}
      />
    </div>
  );
};
